import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import PersonIcon from '@mui/icons-material/Person';
import { UserFromServer } from '../../__generated__/types';
import type { PlanKey } from '../registration/homeowner-plan-selection-fieldset';
import { useHighLevelContext } from '../../hooks/HighLevelContext';
import { EnhancedMembershipsList } from '../../components/memberships-list/enhanced-memberships-list';
import { CustomPaymentModal } from '../registration/custom-payment-modal';
import { useNotification } from '../../contexts/NotificationContext';
import { useUserContextDispatch } from '../../hooks/userContext';

interface BasicPlanDashboardProps {
  user: UserFromServer | null;
  planKey?: PlanKey;
}

const getPlanDisplayLabel = (planKey: PlanKey): string => {
  if (planKey === 'Guild') return 'Guild ($4.99)';
  if (planKey === 'GuildPlus') return 'Guild+';
  return 'Basic (Free)';
};

const getPrimaryUpgradeCtaLabel = (planKey: PlanKey): string => {
  if (planKey === 'Guild') return 'Upgrade to Guild+';
  return 'Upgrade to Guild';
};

const getAlertDescription = (planKey: PlanKey): string => {
  if (planKey === 'Guild') {
    return 'Upgrade to Guild+ to access designer matching, education and chat with matched designers.';
  }
  return 'Upgrade to Guild or Guild+ to access designer matching, project galleries, and more.';
};

const getPremiumFeatures = (planKey: PlanKey) => {
  if (planKey === "GuildPlus") {
    return [
      {
        title: "Smart-Guild Matching",
        description: "Get matched with vetted designers",
        locked: false,
      },
      {
        title: "Curated Project Gallery",
        description: "Access to real-world designer file projects",
        locked: false,
      },
      {
        title: "Idea Books",
        description: "Create and save projects to idea books",
        locked: false,
      },
      {
        title: "Guild Guides",
        description: "Expert design insights and curated educational articles",
        locked: false,
      },
      {
        title: "Direct Messaging",
        description: "Chat with matched designers",
        locked: false,
      },
    ];
  }

  if (planKey === 'Guild') {
    return [
      {
        title: 'Smart-Guild Matching',
        description: 'Get matched with vetted designers',
        locked: true,
      },
      {
        title: 'Curated Project Gallery',
        description: 'Access to real-world designer file projects',
        locked: false,
      },
      {
        title: 'Idea Books',
        description: 'Create and save projects to idea books',
        locked: false,
      },
      {
        title: 'Guild Guides',
        description: 'Expert design insights and curated educational articles',
        locked: false,
      },
      {
        title: 'Direct Messaging',
        description: 'Chat with matched designers',
        locked: true,
      },
    ];
  }

  return [
    {
      title: 'Designer Matching',
      description: 'Get matched with vetted designers',
      locked: true,
    },
    {
      title: 'Project Gallery',
      description: 'Access to real-world design projects',
      locked: true,
    },
    {
      title: 'Educational Content',
      description: 'Design guides and tutorials',
      locked: true,
    },
    {
      title: 'Direct Messaging',
      description: 'Chat with matched designers',
      locked: true,
    },
  ];
};

export const BasicPlanDashboard = ({
  user,
  planKey = 'basic',
}: BasicPlanDashboardProps) => {
  const { getPlanList } = useHighLevelContext();
  const { showSuccess, showError } = useNotification();
  const { updateHomeownerAccountData } = useUserContextDispatch();
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansData, setPlansData] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<any | null>(null);

  const handleUpgrade = () => {
    setUpgradeModalOpen(true);
  };

  const resolvePlanKey = (plan: any): PlanKey | null => {
    const tierRaw = plan?.metadata?.tier || plan?.name || "";
    const tier = String(tierRaw).toLowerCase();
    if (tier.includes("basic")) return "basic";
    if (tier.includes("guild+") || tier.includes("guild plus") || tier.includes("guildplus")) {
      return "GuildPlus";
    }
    if (tier.includes("guild")) return "Guild";
    return null;
  };

  const formatBillingCycle = (value?: string) => {
    if (!value) return "Monthly";
    const lower = value.toLowerCase();
    if (lower.includes("bi")) return "Bi-Annually";
    if (lower.includes("annual")) return "Annually";
    if (lower.includes("month")) return "Monthly";
    return value;
  };

  useEffect(() => {
    if (!upgradeModalOpen || plansData.length) return;
    const loadPlans = async () => {
      const fallbackPlans = [
        {
          _id: 'mock-guild-monthly',
          name: 'Guild',
          description:
            '<p>Ready to see what we are all about? The Guild level gives you full access to our detailed real-world projects from our vetted designers, along with full access to our educational articles.</p><ul><li>Curated Project Gallery</li><li>Guild Guide Articles</li><li>Powerful Filters</li><li>Create &amp; Save to Idea Books</li></ul>',
          pricing: { price: 4.99, period: 'monthly' },
          metadata: { tier: 'Guild' },
          perks: [],
          benefits: [],
        },
        {
          _id: 'mock-guild-plus-annually',
          name: 'Guild Plus',
          description:
            '<p>Premium access with exclusive benefits and advanced features.</p><ul><li>Everything in Guild</li><li>Priority Designer Matching</li><li>Exclusive Design Consultations</li><li>Advanced Project Analytics</li></ul>',
          pricing: { price: 149.99, period: 'annually' },
          metadata: { tier: 'Guild+' },
          perks: [],
          benefits: [],
        },
      ];
      setPlansLoading(true);
      try {
        const response = await getPlanList();
        if (!response.items || response.items.length === 0) {
          setPlansData(fallbackPlans);
        } else {
          setPlansData(response.items || []);
        }
      } catch (error) {
        setPlansData(fallbackPlans);
        showError("Unable to load membership plans. Please try again.");
      } finally {
        setPlansLoading(false);
      }
    };
    loadPlans();
  }, [upgradeModalOpen, plansData.length, getPlanList, showError]);

  const allowedPlanKeys = useMemo(() => {
    if (planKey === "basic") return new Set<PlanKey>(["Guild", "GuildPlus"]);
    if (planKey === "Guild") return new Set<PlanKey>(["GuildPlus"]);
    return new Set<PlanKey>();
  }, [planKey]);

  const paidPlans = useMemo(() => {
    return plansData.filter((plan) => {
      const key = resolvePlanKey(plan);
      return key ? allowedPlanKeys.has(key) : false;
    });
  }, [plansData, allowedPlanKeys]);

  const selectedPlanKey = useMemo(() => {
    return selectedPlan ? resolvePlanKey(selectedPlan) : null;
  }, [selectedPlan]);

  const selectedPlanAmount = selectedPlan?.pricing?.price
    ? Number(selectedPlan.pricing.price)
    : 0;
  const selectedPlanBilling = formatBillingCycle(selectedPlan?.pricing?.period);
  const selectedPlanName = selectedPlan?.metadata?.tier || selectedPlan?.name || "";
  const homeownerId = user?.id ?? (user as any)?._id ?? "";

  const handlePlanSelect = (planId?: string) => {
    if (!planId) {
      setSelectedPlanId(null);
      setSelectedPlan(null);
      return;
    }

    setSelectedPlanId(planId);
    const plan = plansData.find((item) => item._id === planId) || null;
    setSelectedPlan(plan);
  };

  const handlePaymentSuccess = async () => {
    if (!homeownerId) {
      showError("Unable to update your membership. Please contact support.");
      return;
    }

    if (!selectedPlanKey) {
      showError("Please select a membership plan to continue.");
      return;
    }

    try {
      await updateHomeownerAccountData({
        _id: homeownerId,
        subscription: selectedPlanKey,
      });

      localStorage.setItem(`payment_${homeownerId}_${selectedPlanKey}`, 'completed');

      const localUserRaw = localStorage.getItem('user');
      if (localUserRaw) {
        try {
          const localUser = JSON.parse(localUserRaw);
          localUser.subscription = selectedPlanKey;
          localUser.paymentStatus = 'paid';
          localStorage.setItem('user', JSON.stringify(localUser));
        } catch {
          // Ignore localStorage update failures
        }
      }

      showSuccess("Payment successful! Updating your membership...");
      setPaymentModalOpen(false);
      setUpgradeModalOpen(false);
      setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      showError("Payment succeeded, but we couldn't update your membership. Please contact support.");
    }
  };

  const handlePaymentFailure = (message: string) => {
    showError(message || "Payment failed. Please try again.");
  };

  const planLabel = getPlanDisplayLabel(planKey);
  const alertDescription = getAlertDescription(planKey);
  const premiumFeatures = getPremiumFeatures(planKey);
  const hasLockedFeatures = premiumFeatures.some((feature) => feature.locked);
  const primaryUpgradeCtaLabel = getPrimaryUpgradeCtaLabel(planKey);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Your Hub
      </Typography>

      {/* Upgrade Prompt */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: 3, 
          backgroundColor: '#E3F2FD',
          border: '1px solid #2196F3',
          '& .MuiAlert-icon': {
            color: '#1976D2'
          }
        }}
        action={
          <Button 
            color="primary" 
            size="small" 
            onClick={handleUpgrade}
            startIcon={<UpgradeIcon />}
            sx={{ fontWeight: 600 }}
          >
            Upgrade Now
          </Button>
        }
      >
        <Typography variant="body2" fontWeight={600}>
          Unlock Full Access to Home Design Guild Features!
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {alertDescription}
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2} display="flex" alignItems="center" gap={1}>
                <PersonIcon color="primary" />
                Account Information
              </Typography>
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {user?.email}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Plan
                  </Typography>
                  <Typography variant="body1" fontWeight={500} color="primary">
                    {planLabel}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

      {/* Premium Features */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={2} display="flex" alignItems="center" gap={1}>
              {hasLockedFeatures ? (
                <LockIcon color="disabled" />
              ) : (
                <CheckCircleOutlineIcon sx={{ color: '#00A06D' }} />
              )}
              Premium Features
            </Typography>
              
              <Stack spacing={2}>
                {premiumFeatures.map((feature) => (
                  <Box key={feature.title} display="flex" alignItems="center" gap={2}>
                    {feature.locked ? (
                      <LockIcon color="disabled" fontSize="small" />
                    ) : (
                      <CheckCircleOutlineIcon fontSize="small" sx={{ color: '#00A06D' }} />
                    )}
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {feature.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Upgrade CTA */}
      <Card sx={{ mt: 3, backgroundColor: '#F8F9FA', border: '2px solid #E9ECEF' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" fontWeight={700} mb={2} color="primary">
            Ready to Transform Your Space?
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3} sx={{ maxWidth: '600px', mx: 'auto' }}>
            Join thousands of homeowners who have found their perfect designer match. 
            Upgrade today and unlock the full potential of Home Design Guild.
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Button
              variant="contained"
              size="large"
              onClick={handleUpgrade}
              startIcon={<UpgradeIcon />}
              sx={{
                backgroundColor: '#d22268',
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#b51b57',
                },
              }}
            >
              {primaryUpgradeCtaLabel}
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={handleUpgrade}
              sx={{
                borderColor: '#d22268',
                color: '#d22268',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#b51b57',
                  backgroundColor: 'rgba(210, 34, 104, 0.04)',
                },
              }}
            >
              View All Plans
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Select Your Membership</DialogTitle>
        <DialogContent dividers>
          {plansLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: '#00A06D' }} />
            </Box>
          ) : paidPlans.length === 0 ? (
            <Alert severity="warning">
              Unable to load membership options. Please try again later.
            </Alert>
          ) : (
            <EnhancedMembershipsList
              selectedPlan={selectedPlanId}
              onSelect={handlePlanSelect}
              variant="homeowner"
              showPlaceholders={false}
              plans={paidPlans}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setUpgradeModalOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedPlan || !selectedPlanKey) return;
              setUpgradeModalOpen(false);
              setPaymentModalOpen(true);
            }}
            disabled={!selectedPlan || !selectedPlanKey}
            sx={{
              backgroundColor: '#d22268',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#b51b57' },
            }}
          >
            Continue to Payment
          </Button>
        </DialogActions>
      </Dialog>

      {selectedPlan && selectedPlanKey && (
        <CustomPaymentModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentFailure={handlePaymentFailure}
          amount={selectedPlanAmount}
          planName={selectedPlanName}
          planType={selectedPlanKey}
          billingCycle={selectedPlanBilling}
          email={user?.email ?? ''}
          homeownerId={homeownerId}
          customerInfo={{
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            phoneNumber: (user as any)?.phoneNumber ?? '',
          }}
          planData={selectedPlan}
        />
      )}
    </Box>
  );
};
